import {
  Modal,
  Form,
  Button,
  Space,
  Select,
  Input,
  InputNumber,
  Divider,
  Card,
  Row,
  Col,
  message,
} from 'antd';
import { PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import React, { useState, useEffect, useContext } from 'react';
import AddAddressModal from './AddAddressModal';
import { axiosPostCall, axiosGetCall } from '../../api/axiosCall';
import { AuthContext } from '../../context/AuthProvider';
import { API_END_POINT, SOMETHING_WENT_WRONG } from '../../constant/constants';

const CreateOrderModal = ({ isOpen, onClose, onOrderCreated }) => {
  const [form] = Form.useForm();
  const [companies, setCompanies] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [products, setProducts] = useState([]);
  const [spareParts, setSpareParts] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [isAddAddressModalOpen, setIsAddAddressModalOpen] = useState(false);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const [isLoadingAddresses, setIsLoadingAddresses] = useState(false);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [isLoadingSpareParts, setIsLoadingSpareParts] = useState(false);
  const [isSubmittingOrder, setIsSubmittingOrder] = useState(false);

  const { token } = useContext(AuthContext);

  // Fetch companies when modal opens
  const fetchCompanies = async () => {
    try {
      setIsLoadingCompanies(true);
      const res = await axiosPostCall({
        apiEndPoint: API_END_POINT.GET_COMPANY_DROPDOWN_LISTS,
        header: {
          Authorization: `Bearer ${token}`,
          accept: 'text/plain',
        },
        body: {},
      });

      if (res?.hideErrorFromUser && res?.data) {
        const companyOptions = res.data.map((company) => ({
          value: company.id,
          label: company.name,
          // companyData: company
        }));
        setCompanies(companyOptions);
      } else {
        message.error(res?.error?.message || SOMETHING_WENT_WRONG);
        setCompanies([]);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
      message.error(SOMETHING_WENT_WRONG);
      setCompanies([]);
    } finally {
      setIsLoadingCompanies(false);
    }
  };

  // Fetch products when modal opens
  const fetchProducts = async () => {
    try {
      setIsLoadingProducts(true);
      const res = await axiosGetCall({
        apiEndPoint: API_END_POINT.GET_PRODUCT_OPTIONS,
        header: {
          Authorization: `Bearer ${token}`,
          accept: 'text/plain',
        },
        body: {},
      });

      if (res?.hideErrorFromUser && res?.data) {
        const productOptions = res.data.map((product) => ({
          value: product.productId,
          label: product.productName,
          productData: product,
        }));
        setProducts(productOptions);
      } else {
        message.error(res?.error?.message || SOMETHING_WENT_WRONG);
        setProducts([]);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      message.error(SOMETHING_WENT_WRONG);
      setProducts([]);
    } finally {
      setIsLoadingProducts(false);
    }
  };

  // Fetch spare parts when modal opens
  const fetchSpareParts = async () => {
    try {
      setIsLoadingSpareParts(true);
      const res = await axiosGetCall({
        apiEndPoint: API_END_POINT.GET_SPARE_PART_OPTIONS,
        header: {
          Authorization: `Bearer ${token}`,
          accept: 'text/plain',
        },
        body: {},
      });

      if (res?.hideErrorFromUser && res?.data) {
        const sparePartOptions = res.data.map((sparePart) => ({
          value: sparePart.id,
          label: sparePart.name,
          sparePartData: sparePart,
        }));
        setSpareParts(sparePartOptions);
      } else {
        message.error(res?.error?.message || SOMETHING_WENT_WRONG);
        setSpareParts([]);
      }
    } catch (error) {
      console.error('Error fetching spare parts:', error);
      message.error(SOMETHING_WENT_WRONG);
      setSpareParts([]);
    } finally {
      setIsLoadingSpareParts(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      // Reset form and state when modal opens
      form.resetFields();
      setSelectedCompanyId(null);
      setAddresses([]);

      // Fetch data
      fetchCompanies();
      fetchProducts();
      fetchSpareParts();
    }
  }, [isOpen, token, form]);

  // Fetch addresses when company is selected
  const fetchAddresses = async (companyId) => {
    try {
      setIsLoadingAddresses(true);
      const res = await axiosGetCall({
        apiEndPoint: `${API_END_POINT.GET_ADDRESS_LIST_BY_COMPANY}?ComapnyId=${companyId}`,
        header: {
          Authorization: `Bearer ${token}`,
          accept: 'text/plain',
        },
        body: {},
      });

      if (res?.hideErrorFromUser && res?.data) {
        const addressOptions = res.data.map((address) => ({
          value: address.addressId,
          label: `${address.streetAddress}, ${address.city}, ${address.state} ${address.zipCode}`,
          addressData: address,
        }));
        setAddresses(addressOptions);
      } else {
        message.error(res?.error?.message || SOMETHING_WENT_WRONG);
        setAddresses([]);
      }
    } catch (error) {
      console.error('Error fetching addresses:', error);
      message.error(SOMETHING_WENT_WRONG);
      setAddresses([]);
    } finally {
      setIsLoadingAddresses(false);
    }
  };

  useEffect(() => {
    if (selectedCompanyId) {
      fetchAddresses(selectedCompanyId);
    } else {
      setAddresses([]);
    }
  }, [selectedCompanyId, token]);

  const handleCompanyChange = (value) => {
    setSelectedCompanyId(value);
    form.setFieldsValue({ addressId: undefined }); // Reset address when company changes
  };

  const handleAddAddress = () => {
    setIsAddAddressModalOpen(true);
  };

  const handleAddressAdded = (newAddress) => {
    // Add new address to the list
    setAddresses((prev) => [...prev, newAddress]);
    setIsAddAddressModalOpen(false);
    // Auto-select the newly added address
    form.setFieldsValue({ addressId: newAddress.value });
  };

  const handleSubmit = async (values) => {
    try {
      setIsSubmittingOrder(true);

      // Combine products and spare parts into a single array
      const allProducts = [];

      // Add regular products
      if (values.products && values.products.length > 0) {
        values.products.forEach((product) => {
          if (product.productId && product.quantity) {
            allProducts.push({
              productId: product.productId,
              productQty: product.quantity,
              isSparePart: false,
            });
          }
        });
      }

      // Add spare parts
      if (values.spareParts && values.spareParts.length > 0) {
        values.spareParts.forEach((sparePart) => {
          if (sparePart.sparePartId && sparePart.quantity) {
            allProducts.push({
              productId: sparePart.sparePartId,
              productQty: sparePart.quantity,
              isSparePart: true,
            });
          }
        });
      }

      // Require at least one valid product or spare part
      if (allProducts.length === 0) {
        message.error(
          'Please add at least one product or one spare part with quantity.',
        );
        setIsSubmittingOrder(false);
        return;
      }

      // Prepare order data
      const orderData = {
        products: allProducts,
        companyId: values.companyId,
        addressId: values.addressId,
        note: values.orderNotes || '',
      };

      console.log('Order data being sent:', orderData);

      const res = await axiosPostCall({
        apiEndPoint: API_END_POINT.PLACE_ORDER,
        header: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: orderData,
      });

      if (res?.hideErrorFromUser) {
        message.success('Order placed successfully!');

        // Clear all form fields and reset state
        form.resetFields();
        setSelectedCompanyId(null);
        setAddresses([]);

        onClose();
        // Refresh the order list in parent component
        if (onOrderCreated) {
          onOrderCreated();
        }
      } else {
        message.error(res?.error?.message || SOMETHING_WENT_WRONG);
      }
    } catch (error) {
      console.error('Error placing order:', error);
      message.error(SOMETHING_WENT_WRONG);
    } finally {
      setIsSubmittingOrder(false);
    }
  };

  const handleCancel = () => {
    // Clear all form fields and reset state
    form.resetFields();
    setSelectedCompanyId(null);
    setAddresses([]);
    onClose();
  };

  return (
    <>
      <Modal
        title='Create New Order'
        open={isOpen}
        onCancel={handleCancel}
        footer={null}
        width={1000}
        destroyOnClose
      >
        <Form
          form={form}
          layout='vertical'
          onFinish={handleSubmit}
          autoComplete='off'
          initialValues={{
            products: [],
            spareParts: [],
          }}
        >
          {/* Company Selection */}
          <Card
            title='Customer Information'
            size='small'
            className='mb-4'
          >
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  name='companyId'
                  label='Select Company'
                  rules={[
                    { required: true, message: 'Please select a company' },
                  ]}
                >
                  <Select
                    placeholder='Select a company'
                    options={companies}
                    onChange={handleCompanyChange}
                    loading={isLoadingCompanies}
                    showSearch
                    filterOption={(input, option) =>
                      (option?.label ?? '')
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    notFoundContent={
                      isLoadingCompanies
                        ? 'Loading companies...'
                        : 'No companies found'
                    }
                  />
                </Form.Item>
              </Col>
            </Row>

            {/* Address Selection */}
            <Row gutter={16}>
              <Col span={20}>
                <Form.Item
                  name='addressId'
                  label='Delivery Address'
                  rules={[
                    { required: true, message: 'Please select an address' },
                  ]}
                >
                  <Select
                    placeholder='Select delivery address'
                    options={addresses}
                    disabled={!selectedCompanyId}
                    loading={isLoadingAddresses}
                    notFoundContent={
                      isLoadingAddresses
                        ? 'Loading addresses...'
                        : selectedCompanyId
                          ? 'No addresses found'
                          : 'Please select a company first'
                    }
                  />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item label=' '>
                  <Button
                    type='dashed'
                    onClick={handleAddAddress}
                    disabled={!selectedCompanyId}
                    icon={<PlusOutlined />}
                    block
                    className='border-primary text-primary'
                  >
                    Add Address
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* Products Section */}
          <Card
            title='Products'
            size='small'
            className='mb-4'
          >
            <Form.List name='products'>
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <div
                      key={key}
                      className='flex items-end space-x-2 mb-2'
                    >
                      <div className='flex-1'>
                        <Form.Item
                          {...restField}
                          name={[name, 'productId']}
                          label={key === 0 ? 'Product' : ''}
                          rules={[]}
                        >
                          <Select
                            placeholder='Select product'
                            options={products}
                            loading={isLoadingProducts}
                            showSearch
                            filterOption={(input, option) =>
                              (option?.label ?? '')
                                .toLowerCase()
                                .includes(input.toLowerCase())
                            }
                            notFoundContent={
                              isLoadingProducts
                                ? 'Loading products...'
                                : 'No products found'
                            }
                          />
                        </Form.Item>
                      </div>
                      <div className='w-24'>
                        <Form.Item
                          {...restField}
                          name={[name, 'quantity']}
                          label={key === 0 ? 'Quantity' : ''}
                          rules={[]}
                        >
                          <InputNumber
                            min={1}
                            placeholder='Qty'
                            style={{ width: '100%' }}
                          />
                        </Form.Item>
                      </div>
                      <div className='w-8'>
                        {fields.length > 1 && (
                          <MinusCircleOutlined
                            onClick={() => remove(name)}
                            className='text-red-500 cursor-pointer'
                          />
                        )}
                      </div>
                    </div>
                  ))}
                  <Form.Item>
                    <Button
                      type='dashed'
                      onClick={() => add()}
                      block
                      icon={<PlusOutlined />}
                      className='border-primary text-primary'
                    >
                      Add Product
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </Card>

          {/* Spare Parts Section */}
          <Card
            title='Spare Parts'
            size='small'
            className='mb-4'
          >
            <Form.List name='spareParts'>
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <div
                      key={key}
                      className='flex items-end space-x-2 mb-2'
                    >
                      <div className='flex-1'>
                        <Form.Item
                          {...restField}
                          name={[name, 'sparePartId']}
                          label={key === 0 ? 'Spare Part' : ''}
                          rules={[]}
                        >
                          <Select
                            placeholder='Select spare part'
                            options={spareParts}
                            loading={isLoadingSpareParts}
                            showSearch
                            filterOption={(input, option) =>
                              (option?.label ?? '')
                                .toLowerCase()
                                .includes(input.toLowerCase())
                            }
                            notFoundContent={
                              isLoadingSpareParts
                                ? 'Loading spare parts...'
                                : 'No spare parts found'
                            }
                          />
                        </Form.Item>
                      </div>
                      <div className='w-24'>
                        <Form.Item
                          {...restField}
                          name={[name, 'quantity']}
                          label={key === 0 ? 'Quantity' : ''}
                          rules={[]}
                        >
                          <InputNumber
                            min={1}
                            placeholder='Qty'
                            style={{ width: '100%' }}
                          />
                        </Form.Item>
                      </div>
                      <div className='w-8'>
                        {fields.length > 1 && (
                          <MinusCircleOutlined
                            onClick={() => remove(name)}
                            className='text-red-500 cursor-pointer'
                          />
                        )}
                      </div>
                    </div>
                  ))}
                  <Form.Item>
                    <Button
                      type='dashed'
                      onClick={() => add()}
                      block
                      icon={<PlusOutlined />}
                      className='border-primary text-primary'
                    >
                      Add Spare Part
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </Card>

          {/* Order Notes */}
          <Card
            title='Order Information'
            size='small'
            className='mb-4'
          >
            <Form.Item
              name='orderNotes'
              label='Order Notes'
            >
              <Input.TextArea
                rows={4}
                placeholder='Enter any special instructions or notes for this order...'
              />
            </Form.Item>
          </Card>

          <div className='flex justify-end space-x-2 mt-6'>
            <Button
              onClick={handleCancel}
              disabled={isSubmittingOrder}
              className='w-28 bg-surface border border-border text-text hover:text-primary hover:border-primary'
            >
              Cancel
            </Button>
            <Button
              type='primary'
              htmlType='submit'
              loading={isSubmittingOrder}
              className='w-36 border-none bg-gradient-to-r from-blue-500 to-purple-600 shadow hover:shadow-md'
            >
              Create Order
            </Button>
          </div>
        </Form>
      </Modal>

      <AddAddressModal
        isOpen={isAddAddressModalOpen}
        onClose={() => setIsAddAddressModalOpen(false)}
        onAddressAdded={handleAddressAdded}
        companyId={selectedCompanyId}
      />
    </>
  );
};

export default CreateOrderModal;
